import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { isNil } from "ramda";

import { AffinityForm, ConfirmationView, FormView } from "affinityForm";
import { translateErrorMessages, useDryRunMutation, useSubmitMutation } from "affinityForm/helpers";
import { Layout } from "affinityUI/structures/layout";
import {
  PatientConfirmation,
  PatientForm,
  PatientFormData,
  PatientsFormContext,
} from "components/views/patientDetails/patientEdit";
import { patientValidationErrors } from "components/views/patientDetails/patientEdit/patientForm/messages";
import { patientCreateValidationSchema } from "components/views/patientDetails/patientEdit/patientForm/validationSchema";
import { CreatePatient } from "gql";
import { collectGarbage, composeUpdates, invalidatePatientsList } from "gql/invalidations";

const patientVarSetter = (patient: PatientFormData) => ({
  patient,
});

export const CreatePatientPage: React.FC = () => {
  const navigate = useNavigate();
  const update = composeUpdates([invalidatePatientsList, collectGarbage]);

  const validatePatient = useDryRunMutation(CreatePatient, {
    varSetter: patientVarSetter,
    dataGetter: (response) => response?.data?.createPatient,
    parseError: translateErrorMessages(patientValidationErrors),
  });
  const updatePatient = useSubmitMutation(CreatePatient, {
    varSetter: patientVarSetter,
    update,
    onSave: useCallback(
      (data) => {
        if (!isNil(data?.patient?.id)) {
          navigate(`/patients/${data?.patient?.id}`, { replace: true });
        }
      },
      [navigate]
    ),
    dataGetter: (response) => response?.data?.createPatient,
  });

  return (
    <AffinityForm<PatientFormData>
      asyncValidation={validatePatient}
      validationSchema={patientCreateValidationSchema}
      context={PatientsFormContext}
      confirmHandler={updatePatient}
    >
      <FormView>
        <Layout title="Add patient" showBackBtn>
          <PatientForm />
        </Layout>
      </FormView>
      <ConfirmationView>
        <Layout title="Confirm patient" showBackBtn backLink=".." backLinkText="Back to new patient form">
          <PatientConfirmation />
        </Layout>
      </ConfirmationView>
    </AffinityForm>
  );
};
